<template>
    <div class="clicksign-review">
        <div class="clicksign-review-toolbar">
            <div>
                <h5 class="mb-1">Conferir solicitação de assinatura</h5>
                <p class="mb-0 text-muted">Revise os dados antes de enviar a solicitação para assinatura eletrônica.</p>
                <small v-if="warning" class="text-warning font-weight-bold">{{ warning }}</small>
            </div>
            <div class="clicksign-review-actions">
                <button type="button" class="btn btn-outline-secondary" :disabled="loading" @click="$emit('cancel')">
                    Cancelar
                </button>
                <button type="button" class="btn btn-success" :disabled="loading" @click="$emit('confirm')">
                    <span v-if="loading" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    {{ loading ? 'Solicitando assinatura...' : 'Confirmar e solicitar assinatura' }}
                </button>
            </div>
        </div>

        <div class="clicksign-review-body">
            <div class="clicksign-card">
                <h6>Documento</h6>
                <div class="row gutters">
                    <div class="col-md-8 col-12">
                        <label>Nome</label>
                        <input class="form-control" type="text" :value="document.name || '-'" readonly>
                    </div>
                    <div class="col-md-4 col-12">
                        <label>Data Limite de Assinatura</label>
                        <input class="form-control" type="text" :value="formatDate(document.deadline_at)" readonly>
                    </div>
                </div>
            </div>

            <div class="clicksign-card">
                <h6>Signatários</h6>
                <div v-if="signers.length" class="clicksign-signers-grid">
                    <div v-for="(signer, index) in signers" :key="`${signer.email}-${signer.sign_as}-${index}`" class="clicksign-signer-card">
                        <div class="row gutters">
                            <div class="col-12">
                                <label>Nome</label>
                                <input class="form-control" type="text" :value="signer.name || '-'" readonly>
                            </div>
                            <div class="col-12">
                                <label>E-mail</label>
                                <input class="form-control" type="text" :value="signer.email || '-'" readonly>
                            </div>
                            <div class="col-md-6 col-12">
                                <label>CPF/CNPJ</label>
                                <input class="form-control" type="text" :value="signer.documentation || '-'" readonly>
                            </div>
                            <div class="col-md-6 col-12">
                                <label>Papel</label>
                                <input class="form-control" type="text" :value="translateRole(signer.sign_as)" readonly>
                            </div>
                            <div class="col-md-6 col-12">
                                <label>Telefone</label>
                                <input class="form-control" type="text" :value="signer.phone_number || '-'" readonly>
                            </div>
                            <div class="col-md-6 col-12">
                                <label>Token de Confirmação</label>
                                <input class="form-control" type="text" :value="formatAuth(signer.auths)" readonly>
                            </div>
                            <div class="col-12">
                                <label>Exigências de assinatura</label>
                                <div class="clicksign-requirements">
                                    <span v-for="requirement in signatureRequirements(signer)" :key="requirement" class="clicksign-badge" :class="{'is-empty': requirement === 'Nenhuma exigência adicional'}">
                                        {{ requirement }}
                                    </span>
                                </div>
                            </div>
                            <div class="col-12">
                                <label>Mensagem</label>
                                <textarea class="form-control" :value="signer.message || '-'" readonly></textarea>
                            </div>
                        </div>
                    </div>
                </div>
                <p v-else class="mb-0">Nenhum signatário encontrado.</p>
            </div>

            <div class="clicksign-preview">
                <div class="clicksign-preview-title">Visualização do documento</div>
                <iframe v-if="document.content_base64" :src="document.content_base64" title="Pré-visualização do documento"></iframe>
                <div v-else class="alert alert-warning mb-0">Não foi possível carregar a pré-visualização do documento.</div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ClickSignRequestReviewComponent',
    props: {
        review: {
            type: Object,
            default: () => ({}),
        },
        loading: {
            type: Boolean,
            default: false,
        },
        warning: {
            type: String,
            default: '',
        },
    },
    computed: {
        document() {
            return this.review?.document || {};
        },
        signers() {
            return this.review?.signers || [];
        },
    },
    methods: {
        formatDate(value) {
            if (!value) return '-';
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return value;
            return date.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
        formatAuth(value) {
            const auths = Array.isArray(value) ? value : [value];
            return auths.filter(Boolean).map((auth) => ({
                email: 'E-mail',
                sms: 'SMS',
                whatsapp: 'WhatsApp',
                pix: 'Pix',
                api: 'API',
            }[auth] || auth)).join(', ') || '-';
        },
        isActive(value) {
            return value === true || value === 1 || value === '1' || value === 'true' || value === 't';
        },
        signatureRequirements(signer) {
            const requirements = [];
            if (this.isActive(signer.selfie_enabled)) requirements.push('Exige selfie');
            if (this.isActive(signer.handwritten_enabled)) requirements.push('Assinatura na tela');
            if (this.isActive(signer.official_document_enabled)) requirements.push('Foto de documento oficial');
            if (this.isActive(signer.liveness_enabled)) requirements.push('Prova de vida');
            if (this.isActive(signer.facial_biometrics_enabled)) requirements.push('Biometria facial');
            return requirements.length ? requirements : ['Nenhuma exigência adicional'];
        },
        translateRole(role) {
            const roles = {
                account_holder: 'Correntista',
                accountant: 'Contador',
                administrator: 'Administrador',
                approve: 'Aprovar',
                associate: 'Associado(a)',
                attorney: 'Procurador',
                bailee: 'Fiel depositário(a)',
                borrower: 'Comodatário',
                broker: 'Corretor(a)',
                building_manager: 'Síndico(a)',
                buyer: 'Parte compradora',
                collateral_provider: 'Caucionante',
                comforter: 'Comodante',
                consented: 'Anuído',
                consenting: 'Anuente',
                consenting_intervenor: 'Interveniente Anuente',
                consignee: 'Consignatário(a)',
                contractee: 'Contratada',
                contractor: 'Contratante',
                co_responsible: 'Responsável solidário',
                creditor: 'Credor(a)',
                debtor: 'Devedor(es)',
                distracted: 'Distratada',
                distracting: 'Distratante',
                director: 'Diretor(a)',
                employee: 'Empregado(a)',
                employer: 'Empregador(a)',
                endorsee: 'Endossatário',
                endorser: 'Endossante',
                franchisee: 'Franqueado(a)',
                franchisor: 'Franqueador(a)',
                grantee: 'Outorgado(a)',
                grantor: 'Outorgante',
                guarantor: 'Fiador',
                guarantor_spouse: 'Cônjuge do fiador',
                insurance_broker: 'Corretor de seguros',
                insured: 'Segurado(a)',
                intermediary: 'Intermediário(a)',
                intervening: 'Interveniente',
                intervening_guarantor: 'Interveniente Garantidor',
                issuer: 'Emitente',
                joint_debtor: 'Devedor solidário',
                lawyer: 'Advogado(a)',
                legal_guardian: 'Responsável legal',
                legal_representative: 'Representante legal',
                lender: 'Mutuante',
                lessee: 'Locatário',
                lessor: 'Locador',
                licensed: 'Licenciada',
                licensor: 'Licenciante',
                manager: 'Gerente/Coordenador',
                owner: 'Proprietário(a)',
                partner: 'Sócio(a)',
                party: 'Parte',
                pledged: 'Consignado(a)',
                president: 'Presidente',
                ratify: 'Homologar',
                real_estate_broker: 'Corretor de imóveis',
                receipt: 'Acusar recebimento',
                resident: 'Morador(a)',
                secretary: 'Secretário(a)',
                secured: 'Afiançado',
                seller: 'Parte vendedora',
                service_provider: 'Prestador(a) de serviços',
                sign: 'Assinante',
                surveyor: 'Vistoriador',
                surety: 'Fiador',
                transferee: 'Cessionário',
                transferor: 'Cedente',
                treasurer: 'Tesoureiro(a)',
                validator: 'Validador',
                witness: 'Testemunha',
            };
            return roles[role] || role || '-';
        },
    },
};
</script>

<style scoped>
.clicksign-review {
    background: #f8fafc;
}
.clicksign-review-toolbar {
    align-items: center;
    background: #fff;
    border-bottom: 1px solid #d8e2ef;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    padding: 16px 20px;
    position: sticky;
    top: 0;
    z-index: 2;
}
.clicksign-review-actions {
    display: flex;
    gap: 10px;
}
.clicksign-review-body {
    padding: 16px;
}
.clicksign-card,
.clicksign-preview,
.clicksign-signer-card {
    background: #fff;
    border: 1px solid #d8e2ef;
    border-radius: 10px;
    box-shadow: 0 6px 18px rgba(15, 23, 42, .06);
    margin-bottom: 14px;
    padding: 14px;
}
.clicksign-card h6 {
    color: #172554;
    font-weight: 700;
    text-transform: uppercase;
}
.clicksign-card label {
    color: #64748b;
    display: block;
    font-size: 11px;
    font-weight: 700;
    margin: 8px 0 5px;
    text-transform: uppercase;
}
.clicksign-card input,
.clicksign-card textarea {
    background: #f8fafc;
    color: #0f172a;
}
.clicksign-card textarea {
    min-height: 74px;
    resize: vertical;
}
.clicksign-signers-grid {
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
}
.clicksign-requirements {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.clicksign-badge {
    background: #e0f2fe;
    border: 1px solid #bae6fd;
    border-radius: 999px;
    color: #075985;
    font-size: 12px;
    font-weight: 700;
    padding: 7px 10px;
}
.clicksign-badge.is-empty {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #475569;
}
.clicksign-preview-title {
    color: #0f172a;
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 10px;
}
.clicksign-preview iframe {
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    height: 62vh;
    width: 100%;
}
@media (max-width: 760px) {
    .clicksign-review-toolbar,
    .clicksign-review-actions {
        align-items: stretch;
        flex-direction: column;
    }
    .clicksign-signers-grid {
        grid-template-columns: 1fr;
    }
    .clicksign-preview iframe {
        height: 52vh;
    }
}
</style>
